/**
 * Color-coded label chip for jobs (Emergency, Night, Day rate, etc.).
 *
 * Usage:
 *   <LabelChip label={{ name: 'Emergency', color: '#EF4444' }} />
 *   <LabelChip label={...} size="sm" onRemove={() => ...} />
 *
 * Renders a small rounded pill with the label color as background tint and
 * the saturated color as text/border so it stays readable on both light and
 * dark surfaces.
 */
import React from 'react';
import { X } from 'lucide-react';

// Hex (#RRGGBB) → rgba string with a given alpha so we can tint the background
// without preloading a million Tailwind utilities.
function hexToRgba(hex, alpha) {
  if (!hex || hex.length !== 7) return `rgba(100,116,139,${alpha})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export default function LabelChip({ label, size = 'md', onRemove, className = '' }) {
  if (!label) return null;
  const color = label.color || '#94A3B8';
  const isSmall = size === 'sm';
  const padding = isSmall ? 'px-1.5 py-0.5' : 'px-2 py-0.5';
  const text = isSmall ? 'text-[10px]' : 'text-xs';

  return (
    <span
      className={`inline-flex items-center gap-1 rounded font-semibold uppercase tracking-wide ${padding} ${text} ${className}`}
      style={{
        background: hexToRgba(color, 0.15),
        color,
        border: `1px solid ${hexToRgba(color, 0.35)}`,
      }}
      data-testid={`label-chip-${(label.name || '').toLowerCase().replace(/\s+/g, '-')}`}
      title={label.name}
    >
      <span
        className="inline-block rounded-full flex-shrink-0"
        style={{ width: 6, height: 6, background: color }}
        aria-hidden
      />
      <span className="truncate max-w-[120px]">{label.name}</span>
      {onRemove && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="ml-0.5 -mr-0.5 opacity-70 hover:opacity-100"
          aria-label={`Remove ${label.name} label`}
          data-testid={`label-chip-remove-${(label.name || '').toLowerCase().replace(/\s+/g, '-')}`}
        >
          <X className={isSmall ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
        </button>
      )}
    </span>
  );
}
